import * as stylex from '@stylexjs/stylex'
import { useCallback, useEffect, useState } from 'react'

import Avatar from '../../components/Avatar'
import Button from '../../components/Button'
import Dialog from '../../components/Dialog'
import Icon from '../../components/Icon'
import type { Overview, Loan, LoanBalance, Person, CreateLoanInput } from '../../lib/ledger'
import { centsToInput, findPerson, formatMoney, parseCents, v1API } from '../../lib/ledger'

export type LoanWithBalance = { loan: Loan; balance: LoanBalance }

const pulse = stylex.keyframes({
  from: { opacity: 1 },
  '50%': { opacity: 0.55 },
  to: { opacity: 1 },
})

const styles = stylex.create({
  s0: {
    fontSize: "11px",
    fontWeight: 500,
    opacity: 0.75,
  },
  s1: {
    marginTop: "8px",
    fontSize: "21px",
    fontWeight: 600,
    letterSpacing: "-.03em",
  },
  s2: {
    display: "flex",
    flexDirection: "column",
    rowGap: "20px",
  },
  s3: {
    display: "block",
  },
  s4: {
    fontSize: "12px",
    fontWeight: 600,
    color: "#46544a",
  },
  s5: {
    marginTop: "8px",
    height: "44px",
    width: "100%",
    borderRadius: "0.75rem",
    borderWidth: 1,
    borderColor: "#dbe5da",
    backgroundColor: "#fff",
    paddingInline: "12px",
    fontSize: "13px",
    outline: "none",
    ":focus": {
      borderColor: "#75a97c",
    },
  },
  s6: {
    marginTop: "8px",
    height: "44px",
    width: "100%",
    borderRadius: "0.75rem",
    borderWidth: 1,
    borderColor: "#dbe5da",
    paddingInline: "12px",
    fontSize: "15px",
    outline: "none",
    ":focus": {
      borderColor: "#75a97c",
    },
  },
  s7: {
    display: "grid",
    gap: "12px",
    "@media (min-width: 640px)": {
      gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    },
  },
  s8: {
    marginTop: "8px",
    display: "flex",
    height: "44px",
    overflow: "hidden",
    borderRadius: "0.75rem",
    borderWidth: 1,
    borderColor: "#dbe5da",
    ":focus-within": {
      borderColor: "#75a97c",
    },
  },
  s9: {
    minWidth: "0px",
    flex: "1 1 0%",
    paddingInline: "12px",
    fontSize: "13px",
    outline: "none",
  },
  s10: {
    borderLeftWidth: 1,
    borderColor: "#dbe5da",
    backgroundColor: "#f9fbf8",
    paddingInline: "8px",
    fontSize: "12px",
    color: "#4f6255",
    outline: "none",
  },
  s11: {
    display: "flex",
    alignItems: "center",
    borderLeftWidth: 1,
    borderColor: "#dbe5da",
    backgroundColor: "#f9fbf8",
    paddingInline: "12px",
    fontSize: "12px",
    color: "#718077",
  },
  s12: {
    fontWeight: 400,
    color: "#8a958c",
  },
  s13: {
    marginTop: "8px",
    height: "44px",
    width: "100%",
    borderRadius: "0.75rem",
    borderWidth: 1,
    borderColor: "#dbe5da",
    paddingInline: "12px",
    fontSize: "13px",
    outline: "none",
    ":focus": {
      borderColor: "#75a97c",
    },
  },
  s14: {
    borderRadius: "1rem",
    borderWidth: 1,
    borderColor: "#d9e8d8",
    backgroundColor: "#f5fbf3",
    padding: "16px",
  },
  s15: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  s16: {
    fontSize: "12px",
    fontWeight: 600,
    color: "#3d6245",
  },
  s17: {
    marginTop: "4px",
    fontSize: "11px",
    color: "#718075",
  },
  s18: {
    height: "20px",
    width: "20px",
    color: "#4d9360",
  },
  s19: {
    marginTop: "16px",
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: "12px",
    "@media (min-width: 640px)": {
      gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
    },
  },
  s20: {
    fontSize: "10px",
    color: "#7a8b7d",
  },
  s21: {
    marginTop: "4px",
    fontSize: "14px",
    fontWeight: 600,
    color: "#2f7041",
  },
  s22: {
    borderRadius: "0.75rem",
    backgroundColor: "#fff1eb",
    paddingInline: "12px",
    paddingBlock: "10px",
    fontSize: "12px",
    lineHeight: "1.25rem",
    color: "#a44e20",
  },
  s23: {
    display: "flex",
    justifyContent: "end",
    gap: "8px",
    borderTopWidth: 1,
    borderColor: "#edf0eb",
    paddingTop: "16px",
  },
  s24: {
    borderRadius: "0.75rem",
    backgroundColor: "#f4f9f2",
    padding: "16px",
  },
  s25: {
    fontSize: "11px",
    fontWeight: 500,
    color: "#66806c",
  },
  s26: {
    marginTop: "4px",
    fontSize: "23px",
    fontWeight: 600,
    color: "#2d7342",
  },
  s27: {
    marginTop: "8px",
    height: "48px",
    width: "100%",
    borderRadius: "0.75rem",
    borderWidth: 1,
    borderColor: "#dbe5da",
    paddingInline: "12px",
    fontSize: "16px",
    outline: "none",
    ":focus": {
      borderColor: "#75a97c",
    },
  },
  s28: {
    borderRadius: "1rem",
    borderWidth: 1,
    borderColor: "#e1e8e1",
    backgroundColor: "#fff",
    padding: "16px",
    boxShadow: "0 4px 16px rgba(48,70,51,.035)",
    "@media (min-width: 640px)": {
      padding: "20px",
    },
  },
  s29: {
    display: "flex",
    alignItems: "start",
    justifyContent: "space-between",
    gap: "16px",
  },
  s30: {
    display: "flex",
    minWidth: "0px",
    alignItems: "center",
    gap: "12px",
  },
  s31: {
    minWidth: "0px",
  },
  s32: {
    overflow: "hidden",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
    fontSize: "14px",
    fontWeight: 600,
    color: "#283229",
  },
  s33: {
    marginTop: "2px",
    overflow: "hidden",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
    fontSize: "11px",
    color: "#768178",
  },
  s34: {
    marginTop: "20px",
    display: "flex",
    alignItems: "end",
    justifyContent: "space-between",
    gap: "16px",
  },
  s35: {
    fontSize: "11px",
    color: "#778379",
  },
  s36: {
    marginTop: "4px",
    fontSize: "25px",
    fontWeight: 600,
    letterSpacing: "-.035em",
    color: "#27342b",
  },
  s37: {
    textAlign: "right",
    fontSize: "11px",
    lineHeight: "1.25rem",
    color: "#78847b",
  },
  s38: {
    marginTop: "16px",
    display: "grid",
    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
    gap: "8px",
    borderTopWidth: 1,
    borderColor: "#edf0eb",
    paddingTop: "12px",
  },
  s39: {
    fontSize: "10px",
    color: "#849087",
  },
  s40: {
    marginTop: "4px",
    fontSize: "12px",
    fontWeight: 600,
    color: "#425148",
  },
  s41: {
    marginTop: "16px",
    display: "flex",
    height: "40px",
    width: "100%",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    borderRadius: "0.75rem",
    backgroundColor: "#eef6eb",
    fontSize: "12px",
    fontWeight: 600,
    color: "#397b49",
    transition: "color, background-color, border-color, text-decoration-color, fill, stroke, opacity, box-shadow, transform, filter, backdrop-filter",
    ":hover": {
      backgroundColor: "#e2f0df",
    },
  },
  s42: {
    height: "16px",
    width: "16px",
  },
  s43: {
    display: "flex",
    flexDirection: "column",
    rowGap: "24px",
    "@media (min-width: 640px)": {
      display: "flex",
      flexDirection: "column",
      rowGap: "28px",
    },
  },
  s44: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    gap: "20px",
    "@media (min-width: 640px)": {
      flexDirection: "row",
      alignItems: "end",
    },
  },
  s45: {
    fontSize: "13px",
    fontWeight: 500,
    color: "#5b9164",
  },
  s46: {
    marginTop: "4px",
    fontSize: "clamp(1.75rem,3.2vw,2.25rem)",
    fontWeight: 600,
    letterSpacing: "-.045em",
    color: "#202720",
  },
  s47: {
    marginTop: "8px",
    maxWidth: "36rem",
    fontSize: "14px",
    color: "#707b72",
  },
  s48: {
    display: "flex",
    height: "40px",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    borderRadius: "0.75rem",
    backgroundColor: "#3f8955",
    paddingInline: "16px",
    fontSize: "13px",
    fontWeight: 600,
    color: "#fff",
    boxShadow: "0 1px 2px 0 rgb(0 0 0 / 0.05)",
    transition: "color, background-color, border-color, text-decoration-color, fill, stroke, opacity, box-shadow, transform, filter, backdrop-filter",
    ":hover": {
      backgroundColor: "#347c49",
    },
  },
  s49: {
    display: "grid",
    gap: "12px",
    "@media (min-width: 768px)": {
      gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
    },
  },
  s50: {
    display: "grid",
    gap: "16px",
    "@media (min-width: 1024px)": {
      gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    },
  },
  s51: {
    height: "240px",
    borderRadius: "1rem",
    backgroundColor: "#eaf0e8",
    animationName: pulse,
    animationDuration: "1.6s",
    animationTimingFunction: "ease-in-out",
    animationIterationCount: "infinite",
  },
  s52: {
    borderRadius: "1rem",
    borderWidth: 1,
    borderColor: "#eadbd2",
    backgroundColor: "#fff",
    padding: "28px",
    textAlign: "center",
  },
  s53: {
    fontSize: "14px",
    fontWeight: 600,
    color: "#6d4131",
  },
  s54: {
    marginTop: "8px",
    fontSize: "12px",
    color: "#7e756f",
  },
  s55: {
    marginTop: "16px",
    height: "36px",
    borderRadius: "0.75rem",
    backgroundColor: "#eef6eb",
    paddingInline: "16px",
    fontSize: "12px",
    fontWeight: 600,
    color: "#397849",
  },
  s56: {
    borderRadius: "1rem",
    borderWidth: 1,
    borderStyle: "dashed",
    borderColor: "#cbdcca",
    backgroundColor: "#fbfdfb",
    paddingInline: "24px",
    paddingBlock: "56px",
    textAlign: "center",
  },
  s57: {
    marginInline: "auto",
    display: "flex",
    height: "48px",
    width: "48px",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "1rem",
    backgroundColor: "#eef6eb",
    color: "#438254",
  },
  s58: {
    height: "24px",
    width: "24px",
  },
  s59: {
    marginTop: "16px",
    fontSize: "16px",
    fontWeight: 600,
    color: "#2c382e",
  },
  s60: {
    marginInline: "auto",
    marginTop: "8px",
    maxWidth: "24rem",
    fontSize: "13px",
    lineHeight: "1.25rem",
    color: "#78847b",
  },
  s61: {
    marginTop: "20px",
    height: "40px",
    borderRadius: "0.75rem",
    backgroundColor: "#3f8955",
    paddingInline: "16px",
    fontSize: "12px",
    fontWeight: 600,
    color: "#fff",
  },
  s62: {
    position: "fixed",
    bottom: "20px",
    right: "20px",
    zIndex: 120,
    maxWidth: "24rem",
    borderRadius: "0.75rem",
    backgroundColor: "#243c2b",
    paddingInline: "16px",
    paddingBlock: "12px",
    fontSize: "12px",
    fontWeight: 500,
    color: "#fff",
    boxShadow: "0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)",
  },
  s63: {
    marginLeft: "12px",
    color: "color-mix(in srgb, #fff 70%, transparent)",
    ":hover": {
      color: "#fff",
    },
  },
})

const loanVariantStyles = stylex.create({
  status: { borderRadius: '9999px', paddingInline: '10px', paddingBlock: '4px', fontSize: '10px', fontWeight: 600 },
  paid: { backgroundColor: '#edf7ef', color: '#347a47' },
  overdue: { backgroundColor: '#fff1eb', color: '#a44e20' },
  cancelled: { backgroundColor: '#f1f2ef', color: '#68746b' },
  open: { backgroundColor: '#eef4ff', color: '#426b9d' },
  metric: { borderRadius: '1rem', borderWidth: 1, paddingInline: '16px', paddingBlock: '16px' },
  metricGreen: { borderColor: '#d7e9d7', backgroundColor: '#f5fbf3', color: '#2e7744' },
  metricOrange: { borderColor: '#f1ded0', backgroundColor: '#fff9f3', color: '#a45525' },
  metricNeutral: { borderColor: '#e1e8e1', backgroundColor: '#fff', color: '#27342b' },
})
type LoansPageProps = { data: Overview; previewLoans?: LoanWithBalance[] }

function rateLabel(bps: number) {
  return `${(bps / 100).toFixed(2)}% / month`
}

function personFor(data: Overview, id: string) {
  return findPerson(data, id) as Person | undefined
}

function statusLabel(status: Loan['status']) {
  return status === 'OVERDUE' ? 'Overdue' : status.charAt(0) + status.slice(1).toLowerCase()
}

function statusStyle(status: Loan['status']) {
  if (status === 'PAID') return loanVariantStyles.paid
  if (status === 'OVERDUE') return loanVariantStyles.overdue
  if (status === 'CANCELLED') return loanVariantStyles.cancelled
  return loanVariantStyles.open
}

function Metric({ label, value, tone }: { label: string; value: string; tone: 'green' | 'orange' | 'neutral' }) {
  const toneStyle = tone === 'green' ? loanVariantStyles.metricGreen : tone === 'orange' ? loanVariantStyles.metricOrange : loanVariantStyles.metricNeutral
  return <div {...stylex.props(loanVariantStyles.metric, toneStyle)}><p {...stylex.props(styles.s0)}>{label}</p><p {...stylex.props(styles.s1)}>{value}</p></div>
}

export function LoanForm({ data, onClose, onSave }: { data: Overview; onClose: () => void; onSave: (input: CreateLoanInput) => Promise<void> }) {
  const [borrowerID, setBorrowerID] = useState(data.people[0]?.id ?? '')
  const [principal, setPrincipal] = useState('')
  const [feeType, setFeeType] = useState<'fixed' | 'percentage'>('fixed')
  const [fee, setFee] = useState('0')
  const [interest, setInterest] = useState('0')
  const [dueAt, setDueAt] = useState('')
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)
  const [today] = useState(() => Date.now())
  const principalCents = parseCents(principal) ?? 0
  const feeValue = Number(fee.replace(',', '.')) || 0
  const interestValue = Number(interest.replace(',', '.')) || 0
  const feeCents = feeType === 'fixed' ? (parseCents(fee) ?? 0) : Math.round(principalCents * feeValue / 100)
  const months = dueAt ? Math.max(1, Math.ceil((new Date(`${dueAt}T23:59:59`).getTime() - today) / (1000 * 60 * 60 * 24 * 30.4375))) : 12
  const interestCents = Math.round(principalCents * (interestValue / 100) * months)
  const totalDueCents = principalCents + feeCents + interestCents
  const monthlyPaymentCents = Math.ceil(totalDueCents / months)

  async function submit(event: React.FormEvent) {
    event.preventDefault()
    setError('')
    const principalCents = parseCents(principal)
    if (!borrowerID || principalCents === null || principalCents <= 0) {
      setError('Choose who borrowed the money and enter a principal amount.')
      return
    }
    const feeValue = Number(fee.replace(',', '.'))
    const interestValue = Number(interest.replace(',', '.'))
    if (!Number.isFinite(feeValue) || feeValue < 0 || !Number.isFinite(interestValue) || interestValue < 0 || interestValue > 100) {
      setError('Fees must be zero or more. Interest must be between 0% and 100%.')
      return
    }
    setSaving(true)
    try {
      await onSave({
        lenderPersonId: data.currentUser.id,
        borrowerPersonId: borrowerID,
        principalCents,
        currency: data.currentUser.currency,
        feeType,
        feeAmountCents: feeType === 'fixed' ? parseCents(fee) ?? 0 : undefined,
        feeRateBps: feeType === 'percentage' ? Math.round(feeValue * 100) : undefined,
        monthlyInterestBps: Math.round(interestValue * 100),
        accrual: 'daily',
        dueAt: dueAt ? new Date(`${dueAt}T23:59:59`).toISOString() : undefined,
      })
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Could not create the loan.')
    } finally {
      setSaving(false)
    }
  }

  return <Dialog description="Keep the principal, fees, and interest transparent for everyone." onClose={onClose} title="Create a loan"><form {...stylex.props(styles.s2)} onSubmit={submit}>
    <label {...stylex.props(styles.s3)}><span {...stylex.props(styles.s4)}>Borrower</span><select {...stylex.props(styles.s5)} onChange={(event) => setBorrowerID(event.target.value)} value={borrowerID}>{data.people.filter((person) => person.id !== data.currentUser.id).map((person) => <option key={person.id} value={person.id}>{person.name}</option>)}</select></label>
    <label {...stylex.props(styles.s3)}><span {...stylex.props(styles.s4)}>Principal ({data.currentUser.currency})</span><input autoFocus {...stylex.props(styles.s6)} data-dialog-autofocus inputMode="decimal" onChange={(event) => setPrincipal(event.target.value)} placeholder="0.00" value={principal} /></label>
    <div {...stylex.props(styles.s7)}><label {...stylex.props(styles.s3)}><span {...stylex.props(styles.s4)}>Fee</span><div {...stylex.props(styles.s8)}><input {...stylex.props(styles.s9)} inputMode="decimal" onChange={(event) => setFee(event.target.value)} value={fee} /><select aria-label="Fee type" {...stylex.props(styles.s10)} onChange={(event) => setFeeType(event.target.value as 'fixed' | 'percentage')} value={feeType}><option value="fixed">{data.currentUser.currency}</option><option value="percentage">%</option></select></div></label><label {...stylex.props(styles.s3)}><span {...stylex.props(styles.s4)}>Monthly interest</span><div {...stylex.props(styles.s8)}><input {...stylex.props(styles.s9)} inputMode="decimal" onChange={(event) => setInterest(event.target.value)} value={interest} /><span {...stylex.props(styles.s11)}>%</span></div></label></div>
    <label {...stylex.props(styles.s3)}><span {...stylex.props(styles.s4)}>Due date <span {...stylex.props(styles.s12)}>(optional)</span></span><input {...stylex.props(styles.s13)} onChange={(event) => setDueAt(event.target.value)} type="date" value={dueAt} /></label>
    {principalCents > 0 && <section {...stylex.props(styles.s14)}><div {...stylex.props(styles.s15)}><div><p {...stylex.props(styles.s16)}>Repayment schedule</p><p {...stylex.props(styles.s17)}>{dueAt ? `${months} monthly payments until ${new Date(`${dueAt}T23:59:59`).toLocaleDateString()}` : 'Estimated over 12 months until you add a due date'}</p></div><Icon {...stylex.props(styles.s18)} name="calendar" /></div><div {...stylex.props(styles.s19)}><div><p {...stylex.props(styles.s20)}>Per month</p><p {...stylex.props(styles.s21)}>{formatMoney(monthlyPaymentCents, data.currentUser.currency)}</p></div><div><p {...stylex.props(styles.s20)}>Interest</p><p {...stylex.props(styles.s21)}>{formatMoney(interestCents, data.currentUser.currency)}</p></div><div><p {...stylex.props(styles.s20)}>Fees</p><p {...stylex.props(styles.s21)}>{formatMoney(feeCents, data.currentUser.currency)}</p></div><div><p {...stylex.props(styles.s20)}>Total due</p><p {...stylex.props(styles.s21)}>{formatMoney(totalDueCents, data.currentUser.currency)}</p></div></div></section>}
    {error && <p {...stylex.props(styles.s22)} role="alert">{error}</p>}
    <div {...stylex.props(styles.s23)}><Button onClick={onClose} variant="secondary" type="button">Cancel</Button><Button disabled={saving} type="submit">{saving ? 'Saving…' : 'Create loan'}</Button></div>
  </form></Dialog>
}

export function PaymentForm({ item, onClose, onSave }: { item: LoanWithBalance; onClose: () => void; onSave: (loanID: string, amountCents: number) => Promise<void> }) {
  const [amount, setAmount] = useState('')
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)
  async function submit(event: React.FormEvent) {
    event.preventDefault()
    const amountCents = parseCents(amount)
    if (amountCents === null || amountCents <= 0 || amountCents > item.balance.outstandingCents) {
      setError(`Enter an amount up to ${formatMoney(item.balance.outstandingCents, item.loan.currency)}.`)
      return
    }
    setSaving(true)
    try { await onSave(item.loan.id, amountCents) } catch (reason) { setError(reason instanceof Error ? reason.message : 'Could not record that payment.') } finally { setSaving(false) }
  }
  return <Dialog description="Payments are allocated to interest, then fees, then principal." onClose={onClose} title="Record a repayment"><form {...stylex.props(styles.s2)} onSubmit={submit}><div {...stylex.props(styles.s24)}><p {...stylex.props(styles.s25)}>Outstanding balance</p><p {...stylex.props(styles.s26)}>{formatMoney(item.balance.outstandingCents, item.loan.currency)}</p></div><label {...stylex.props(styles.s3)}><span {...stylex.props(styles.s4)}>Payment amount</span><input autoFocus {...stylex.props(styles.s27)} data-dialog-autofocus inputMode="decimal" onChange={(event) => setAmount(event.target.value)} placeholder={centsToInput(item.balance.outstandingCents)} value={amount} /></label>{error && <p {...stylex.props(styles.s22)} role="alert">{error}</p>}<div {...stylex.props(styles.s23)}><Button onClick={onClose} variant="secondary" type="button">Cancel</Button><Button disabled={saving} type="submit">{saving ? 'Saving…' : 'Record payment'}</Button></div></form></Dialog>
}

function LoanCard({ data, item, onPay }: { data: Overview; item: LoanWithBalance; onPay: () => void }) {
  const borrower = personFor(data, item.loan.borrowerPersonId)
  const lender = personFor(data, item.loan.lenderPersonId)
  return <article {...stylex.props(styles.s28)}><div {...stylex.props(styles.s29)}><div {...stylex.props(styles.s30)}><Avatar person={borrower ?? { id: item.loan.borrowerPersonId, name: 'Borrower', color: 1 }} size={42} /><div {...stylex.props(styles.s31)}><p {...stylex.props(styles.s32)}>{borrower?.name ?? 'Borrower'}</p><p {...stylex.props(styles.s33)}>Lent by {lender?.name ?? 'you'}</p></div></div><span {...stylex.props(loanVariantStyles.status, statusStyle(item.loan.status))}>{statusLabel(item.loan.status)}</span></div><div {...stylex.props(styles.s34)}><div><p {...stylex.props(styles.s35)}>Outstanding</p><p {...stylex.props(styles.s36)}>{formatMoney(item.balance.outstandingCents, item.loan.currency)}</p></div><p {...stylex.props(styles.s37)}>{rateLabel(item.loan.monthlyInterestBps)}<br />{item.loan.dueAt ? `Due ${new Date(item.loan.dueAt).toLocaleDateString('en', { month: 'short', day: 'numeric', year: 'numeric' })}` : 'No due date'}</p></div><div {...stylex.props(styles.s38)}><div><p {...stylex.props(styles.s39)}>Principal</p><p {...stylex.props(styles.s40)}>{formatMoney(item.balance.principalOutstandingCents, item.loan.currency)}</p></div><div><p {...stylex.props(styles.s39)}>Fees</p><p {...stylex.props(styles.s40)}>{formatMoney(item.balance.feeOutstandingCents, item.loan.currency)}</p></div><div><p {...stylex.props(styles.s39)}>Interest</p><p {...stylex.props(styles.s40)}>{formatMoney(item.balance.interestOutstandingCents, item.loan.currency)}</p></div></div>{item.balance.outstandingCents > 0 && item.loan.status !== 'CANCELLED' && <button {...stylex.props(styles.s41)} onClick={onPay} type="button"><Icon {...stylex.props(styles.s42)} name="banknote" />Record repayment</button>}</article>
}

export default function LoansPage({ data, previewLoans }: LoansPageProps) {
  const [items, setItems] = useState<LoanWithBalance[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [showCreate, setShowCreate] = useState(false)
  const [paying, setPaying] = useState<LoanWithBalance | null>(null)
  const [toast, setToast] = useState('')

  const load = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const loans = (await v1API.listLoans()) ?? []
      const balances = await Promise.all(loans.map(async (loan) => ({ loan, balance: await v1API.getLoanBalance(loan.id) })))
      setItems(balances)
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Could not load loans.')
    } finally { setLoading(false) }
  }, [])

  useEffect(() => {
    if (previewLoans) {
      const timeout = window.setTimeout(() => {
        setItems(previewLoans)
        setLoading(false)
      }, 0)
      return () => window.clearTimeout(timeout)
    }
    const timeout = window.setTimeout(() => { void load() }, 0)
    return () => window.clearTimeout(timeout)
  }, [load, previewLoans])

  async function createLoan(input: CreateLoanInput) {
    await v1API.createLoan(input)
    setShowCreate(false)
    setToast('Loan created. Daily interest will be tracked automatically.')
    await load()
  }

  async function createPayment(loanID: string, amountCents: number) {
    await v1API.createLoanPayment(loanID, { amountCents, method: 'manual' })
    setPaying(null)
    setToast('Repayment recorded and allocated to the loan balance.')
    await load()
  }

  const active = items.filter((item) => item.loan.status === 'ACTIVE' || item.loan.status === 'OVERDUE')
  const outstanding = active.reduce((sum, item) => sum + item.balance.outstandingCents, 0)
  const interest = active.reduce((sum, item) => sum + item.balance.interestOutstandingCents, 0)
  const currencies = new Set(active.map((item) => item.loan.currency))
  const displayCurrency = currencies.size === 1 ? [...currencies][0] : data.currentUser.currency

  return <div {...stylex.props(styles.s43)}><header {...stylex.props(styles.s44)}><div><p {...stylex.props(styles.s45)}>Borrowing, made clear</p><h1 {...stylex.props(styles.s46)}>Loans</h1><p {...stylex.props(styles.s47)}>Track principal, fees, and daily interest without awkward calculations.</p></div><button {...stylex.props(styles.s48)} onClick={() => setShowCreate(true)} type="button"><Icon {...stylex.props(styles.s42)} name="plus" />New loan</button></header>
    <section {...stylex.props(styles.s49)}><Metric label="Open loans" value={`${active.length}`} tone="neutral" /><Metric label="Outstanding" value={currencies.size === 1 ? formatMoney(outstanding, displayCurrency) : `${currencies.size} currencies`} tone="green" /><Metric label="Accrued interest" value={currencies.size === 1 ? formatMoney(interest, displayCurrency) : `${currencies.size} currencies`} tone="orange" /></section>
    {loading ? <div {...stylex.props(styles.s50)}><div {...stylex.props(styles.s51)} /><div {...stylex.props(styles.s51)} /></div> : error ? <section {...stylex.props(styles.s52)}><p {...stylex.props(styles.s53)}>We could not load your loans</p><p {...stylex.props(styles.s54)}>{error}</p><button {...stylex.props(styles.s55)} onClick={() => void load()} type="button">Try again</button></section> : active.length === 0 ? <section {...stylex.props(styles.s56)}><span {...stylex.props(styles.s57)}><Icon {...stylex.props(styles.s58)} name="loans" /></span><h2 {...stylex.props(styles.s59)}>No open loans yet</h2><p {...stylex.props(styles.s60)}>Create a loan when money changes hands. SettleUp will keep every repayment and interest accrual visible.</p><button {...stylex.props(styles.s61)} onClick={() => setShowCreate(true)} type="button">Create your first loan</button></section> : <section {...stylex.props(styles.s50)}>{active.map((item) => <LoanCard data={data} item={item} key={item.loan.id} onPay={() => setPaying(item)} />)}</section>}
    {showCreate && <LoanForm data={data} onClose={() => setShowCreate(false)} onSave={createLoan} />}{paying && <PaymentForm item={paying} onClose={() => setPaying(null)} onSave={createPayment} />}{toast && <div aria-live="polite" {...stylex.props(styles.s62)}>{toast}<button aria-label="Dismiss" {...stylex.props(styles.s63)} onClick={() => setToast('')} type="button">×</button></div>}
  </div>
}
